import { PropsWithChildren, Suspense, useMemo } from 'react';
import { AppBar, Box, Button, CircularProgress, Toolbar, Tooltip, Typography } from '@mui/material';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import CVALogo from '@components/CVALogo';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { EMPTY_STRING, PROJECT_ROLES } from '@constants';
import { TranslationSetupDialog } from '@features/TranslationSetupDialog';
import { setSetupLanguageDialog } from '@store/reducers/project.reducer';
import { useGetUserPermissionsQuery } from '@services/api/homepage.api';
import { SnackbarProvider } from 'notistack';
import { StyledMaterialDesignContent } from '@styles/reusableComponents.styled';

import styles from './Header.module.scss';
import { StyledContainer } from './Header.styled';

const Header = ({ children }: PropsWithChildren) => {
  const { getTranslation } = useCustomTranslation();
  const { isParticipantMode } = useAppSelector((state) => state.user);
  const { userRole } = useAppSelector((state) => state.projects);
  const dispatch = useAppDispatch();

  const { data: permissions } = useGetUserPermissionsQuery(undefined, { skip: isParticipantMode });

  const isAdmin = useMemo(() => {
    return permissions?.payload.isPimsAdmin || userRole === PROJECT_ROLES.ADMIN;
  }, [permissions, userRole]);

  const openAddLanguage = () => {
    dispatch(setSetupLanguageDialog({ isOpen: true, changedValues: {} }));
  };

  // const openUserGuide = () =>
  //   openBrowserWindow({ url: USER_GUIDE_LINK, title: getTranslation('CvaInput.Uguide', 'userGuide') });

  return (
    <StyledContainer className={styles.layout}>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        Components={{
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
        }}
      >
        <AppBar position="sticky" className={styles.header} color="transparent">
          <Toolbar className={styles.toolbar}>
            <CVALogo />
            <Typography variant="subtitle16" className={styles.title}>
              {getTranslation('CvaProj.Title', 'title')}
            </Typography>
            {isAdmin && (
              <Tooltip arrow title={getTranslation('CvaProj.Setup58', 'addLanguage.tooltip')} placement="left">
                <Button startIcon={<GlobeAltIcon width={18} />} onClick={openAddLanguage}>
                  {getTranslation('CvaProj.Setup57', 'addLanguage.title')}
                </Button>
              </Tooltip>
            )}
            {/* <Button onClick={openUserGuide} startIcon={<BookOpenIcon width={18} />}>
              {getTranslation('CvaInput.Uguide', 'userGuide')}
            </Button> */}
          </Toolbar>
        </AppBar>
        <Box component="main" className={`${styles.content} ${isParticipantMode ? EMPTY_STRING : styles.withAppBar}`}>
          <Suspense fallback={<CircularProgress />}>{children}</Suspense>
        </Box>
        <TranslationSetupDialog />
      </SnackbarProvider>
    </StyledContainer>
  );
};

export default Header;
